import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import {
  parseFileCard,
  projectFileDelete,
  type Attachment,
  type ProjectMessage,
} from '../transport/api'
import { PixelPet } from '../identity/PixelPet'
import { AttachmentCard } from './Attachment'
import { Icon } from '../ui/Icon'

/**
 * #projFiles — the files shared in this project, in HISTORICAL ORDER.
 *
 * "The files", not "every file": the list is built from a page of history (500
 * messages, ProjectDockFiles), because there is no endpoint that lists a project's
 * files. A project older than that shows its recent ones. Said here rather than
 * claimed away, since this comment used to promise every file and did not deliver it.
 *
 * There is no list endpoint: the roster is derived from the message timeline, which is
 * exactly why the order is chronological. Each entry keeps its uploader and timestamp,
 * so a file stays attributable even after the conversation has scrolled far past it.
 *
 * PORTALED TO BODY, and that is what makes it a cover panel at all — owner,
 * 2026-08-22: 手機窄版狀態用蓋板的方式呈現吧。就連 Text edit 輸入欄位都可以蓋過.
 *
 * Rendered where it was summoned, it is a child of #projPanel, which is
 * `position: absolute; z-index: 16` — its own stacking context. So this drawer's
 * z-index of 47 was compared INSIDE that box, and the box lost to #footbar's 20. The
 * measurement that showed it: `elementFromPoint` at the composer's top edge returned
 * #footbar while the drawer sat at 47. A z-index cannot climb out of its parent's
 * context, so no number here would ever have covered the composer. Portaling to body
 * puts it in the root context, where 47 means what it says.
 *
 * (`ui/Sheet` was portaled for the neighbouring reason: a modal's rendering must not
 * depend on which corner of the page opened it.)
 */

export type FileEntry = {
  att: Attachment
  messageId: string
  ts: number
  who: string
  agentHash: string | null
  mine: boolean
}

/** Flatten messages into file entries, oldest first. */
export function collectFiles(
  messages: ProjectMessage[],
  selfEmail: string | null,
): FileEntry[] {
  const out: FileEntry[] = []
  for (const m of messages) {
    const card = parseFileCard(m.body)
    if (!card?.attachments?.length) continue
    const agentHash = typeof m.from_ref?.agent === 'string' ? m.from_ref.agent : null
    const who =
      m.from_ref?.label ?? m.from_ref?.email ?? (agentHash ? agentHash.slice(0, 8) : 'unknown')
    const mine = !agentHash && !!selfEmail && m.from_ref?.email === selfEmail
    for (const att of card.attachments) {
      out.push({ att, messageId: m.message_id, ts: m.ts, who, agentHash, mine })
    }
  }
  return out
}

function when(ts: number): string {
  return new Date(ts * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function initial(name: string): string {
  return (name.trim()[0] ?? '?').toUpperCase()
}

export function ProjectFiles({
  projectId,
  files,
  onClose,
  onOpenDoc,
  onDeleted,
}: {
  projectId: string
  files: FileEntry[]
  onClose: () => void
  onOpenDoc: (a: { url: string; name: string; mime: string }) => void
  onDeleted: () => void
}) {
  const del = useMutation({
    mutationFn: (f: FileEntry) => projectFileDelete(projectId, f.messageId, f.att.file_id),
    onSuccess: onDeleted,
  })

  /*
   * OPENING IT PUTS THE KEYBOARD AWAY — owner: 啟用 Project chat file browser 時可以
   * 自動隱藏虛擬鍵盤.
   *
   * On a phone the composer usually has focus when you reach for this, and the soft
   * keyboard takes half the screen — over a panel that is now covering the composer
   * anyway, so the keyboard would be sitting on top of a list it cannot type into.
   * Blurring the focused editable is the only thing that dismisses it; there is no
   * API to hide a keyboard directly.
   *
   * Only an editable is blurred. Taking focus off whatever else happened to have it
   * would break the keyboard-navigation trail for someone who never had a soft
   * keyboard to begin with.
   */
  useEffect(() => {
    const el = document.activeElement
    if (!(el instanceof HTMLElement)) return
    const editable =
      el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement || el.isContentEditable
    if (editable) el.blur()
  }, [])

  return createPortal(
    <aside id="projFiles" className="show" aria-label="Project files">
      <div id="projFilesHd">
        <span className="pf-hd-title">Files</span>
        <button type="button" className="pf-hd-x" onClick={onClose} aria-label="Close files">
          <Icon name="close" size={15} label="Close files" />
        </button>
      </div>

      <div id="projFilesBody" role="list">
        {files.length === 0 && <div className="pm-empty">No files shared yet.</div>}

        {files.map((f) => (
          <div className="pf-item" role="listitem" key={`${f.messageId}:${f.att.file_id}`}>
            <div className="pf-uploader">
              <span className={`pf-up-av${f.agentHash ? ' pet' : ''}`}>
                {/* 17 in a 22px frame: same overflow:hidden, same 1.25 body scale, so the
                    clearance is the same (22 - S)/2 >= 8.25 * S/64 → S <= 17.5. */}
                {f.agentHash ? <PixelPet name={f.who} seed={f.agentHash} size={17} /> : initial(f.who)}
              </span>
              <span className="pf-who" title={f.who}>
                {f.who}
              </span>
              <span className="pf-when">{when(f.ts)}</span>
              <button
                type="button"
                className="pf-del"
                title="Retract this file"
                aria-label={`Retract ${f.att.name ?? 'file'}`}
                disabled={del.isPending}
                onClick={() => del.mutate(f)}
              >
                <Icon name="delete" size={13} label="Retract" />
              </button>
            </div>

            <AttachmentCard
              att={f.att}
              projectId={projectId}
              onOpenDoc={onOpenDoc}
            />
          </div>
        ))}

        {del.error && <div className="pm-empty sb-err">{String(del.error)}</div>}
      </div>
    </aside>,
    document.body,
  )
}
